import { useCallback, useEffect, useMemo, useState } from "react";
import { browser } from "wxt/browser";
import {
  countVisit,
  freshOnboarding,
  type Onboarding,
  type OnboardingStore,
  onboardingKey,
  parseOnboarding,
} from "@/lib/home/onboarding";

/** `chrome.storage.local` in the extension, localStorage on the web app. */
function defaultStore(): OnboardingStore {
  const local = (globalThis as { chrome?: { storage?: { local?: unknown } } }).chrome?.storage
    ?.local;
  if (local)
    return {
      get: async (k) => (await browser.storage.local.get(k))[k],
      set: (k, v) => browser.storage.local.set({ [k]: v }),
    };
  return {
    get: async (k) => {
      try {
        return JSON.parse(localStorage.getItem(k) ?? "null");
      } catch {
        return null;
      }
    },
    set: async (k, v) => {
      try {
        localStorage.setItem(k, JSON.stringify(v));
      } catch {}
    },
  };
}

/**
 * This page load's visit, counted once per person even if the home mounts again (or twice at
 * once): every caller waits on the same read and write.
 */
const visits = new Map<string, Promise<Onboarding>>();

function countThisVisit(userId: string, store: OnboardingStore): Promise<Onboarding> {
  let p = visits.get(userId);
  if (!p) {
    const key = onboardingKey(userId);
    p = (async () => {
      const s = countVisit(parseOnboarding(await store.get(key).catch(() => null)));
      await store.set(key, s).catch(() => {});
      return s;
    })();
    visits.set(userId, p);
  }
  return p;
}

/**
 * The board's onboarding state for this person (ADMIN-263). Counts this new tab as a visit once.
 * `null` until read, so nothing flashes in.
 */
export function useHomeOnboarding(userId: string, store: OnboardingStore = defaultStore()) {
  const [state, setState] = useState<Onboarding | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the store is fixed per page
  useEffect(() => {
    if (!userId) return;
    let live = true;
    void countThisVisit(userId, store).then((s) => live && setState(s));
    return () => {
      live = false;
    };
  }, [userId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the store is fixed per page
  const change = useCallback(
    (patch: Partial<Onboarding>) =>
      setState((prev) => {
        const next = { ...(prev ?? freshOnboarding), ...patch };
        void store.set(onboardingKey(userId), next).catch(() => {});
        visits.set(userId, Promise.resolve(next));
        return next;
      }),
    [userId],
  );

  return useMemo(() => ({ state, change }), [state, change]);
}
